/**
 * ReportPage — `/reports` route. Two tabs (Current Date / Day Wise) with
 * URL-encoded state for shareability.
 *
 * Phase I (CR-0015 / Δ-14). Discovery + plan in /app/docs.
 * CR-0021 (Δ-20) — mandatory clauses only: backend report endpoints now
 *   filter to `type="waiting"` by default. UI is unchanged here; the
 *   filter happens server-side.
 * CR-0024 (Δ-22) — the optional "Include occupied durations" toggle
 *   from CR-0021 was rolled back: no `include_occupied` URL param,
 *   no toggle row, no `typeFilter` threading. Backend default
 *   (`type="waiting"`) is the single source of truth for the report
 *   filter.
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import ReportTabs from "@/components/report/ReportTabs";
import DateRangeCard from "@/components/report/DateRangeCard";
import SummaryGrid from "@/components/report/SummaryGrid";
import RoomCard from "@/components/report/RoomCard";
import DayWiseTable from "@/components/report/DayWiseTable";
import EmptyState from "@/components/report/EmptyState";
import LoadingSkeleton from "@/components/report/LoadingSkeleton";
import { useReport } from "@/hooks/useReport";
import { todayIso } from "@/utils/fmtTime";

export default function ReportPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const tab = params.get("tab") === "daywise" ? "daywise" : "current";
  const from = params.get("from") || todayIso();
  const to = params.get("to") || todayIso();

  // On first mount, ensure URL has defaults.
  useEffect(() => {
    if (!params.get("from") || !params.get("to") || !params.get("tab")) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      next.set("from", from);
      next.set("to", to);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
  };

  const setRange = (newFrom, newTo) => {
    const next = new URLSearchParams(params);
    next.set("from", newFrom);
    next.set("to", newTo);
    setParams(next, { replace: true });
  };

  const setTab = (newTab) => setParam("tab", newTab);

  const { data, isLoading, error } = useReport(tab, from, to);

  return (
    <div
      data-testid="report-page"
      className="min-h-screen"
      style={{ backgroundColor: "#F3F4F6" }}
    >
      {/* Header with back arrow + page title (CR-0015a Δ-14a: drop "Back" text). */}
      <div
        className="flex items-center px-4 py-3 bg-white border-b"
        style={{ borderColor: "#E5E7EB" }}
      >
        <button
          data-testid="report-back"
          onClick={() => navigate("/")}
          className="flex items-center justify-center rounded-md p-2 -ml-2 hover:bg-gray-100 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={22} style={{ color: "#111827" }} />
        </button>
        <div className="ml-2 text-lg font-bold" style={{ color: "#111827" }}>
          Report
        </div>
      </div>

      <ReportTabs value={tab} onChange={setTab} />

      <DateRangeCard
        from={from}
        to={to}
        onFromChange={(v) => setParam("from", v)}
        onToChange={(v) => setParam("to", v)}
        tab={tab}
        onRangeChange={setRange}
      />

      {isLoading && <LoadingSkeleton />}

      {error && (
        <div data-testid="report-error" className="mx-4 mt-6 text-sm text-red-600">
          Failed to load report: {String(error?.message || error)}
        </div>
      )}

      {!isLoading && !error && data && tab === "current" && (
        <>
          <SummaryGrid summary={data.summary} />
          {(!data.rooms || data.rooms.length === 0) ? (
            <EmptyState onTryWeek={setRange} />
          ) : (
            data.rooms.map((room) => (
              <RoomCard
                key={room.code}
                room={room}
                defaultExpanded={false}
              />
            ))
          )}
        </>
      )}

      {!isLoading && !error && data && tab === "daywise" && (
        <>
          {(!data.days || data.days.length === 0) ? (
            <EmptyState onTryWeek={setRange} />
          ) : (
            <DayWiseTable days={data.days} />
          )}
        </>
      )}

      <div className="h-8" />
    </div>
  );
}
